/**
 * The "use another way" half of the CP second sign-in step (kernel apps stage 5, WS-31b, issue
 * #3806, rulings R-149, R-150 and R-152).
 *
 * An operator whose passkey is lost or unusable passes the first factor (a password or Google) and
 * lands on the second-factor page with an `assert` wait. `SecondFactorPage` offers "Use another way"
 * when the wait names recovery proofs, and renders `SecondFactorRecoveryCard` for the chooser and
 * for each proof:
 *
 * - `code`: a recovery code from the ten the CP showed once (WS-31a1);
 * - `email`: a six-digit code that the CP mails to the operator's own address (WS-31a3);
 * - `break-glass`: a one-time token that `bin/cp-break-glass` printed (WS-31a2);
 * - `admin-reset`: no form. The text tells the operator to ask a second platform administrator for
 *   a reset of the passkeys on the person page (R-046).
 *
 * A passed proof never makes a session. The server answers a `recovery_enrol` wait, `useAuth` swaps
 * the token, and the page renders the `register` arm: the operator registers a new passkey, and the
 * verify of that passkey makes the session (R-152). So every proof replaces the passkey step and
 * never the first factor (R-149).
 *
 * `RecoveryCodesCard` shows the ten codes that a verify issued, once, before the console opens or
 * the browser leaves (R-152 Q-5).
 *
 * Security rules: the page never holds the wait token (`useAuth` keeps it in a ref). A proof the
 * console can see is malformed (`recoveryCodeKey` answers `null`, a token outside
 * `BREAK_GLASS_TOKEN_PATTERN`, a mail code outside `RECOVERY_EMAIL_CODE_PATTERN`) sends no request
 * and costs none of the five misses that the server allows per wait. The server text of a refusal
 * never reaches the screen; each refusal word maps to one fixed sentence.
 */
import { useState } from 'react';
import {
  BREAK_GLASS_TOKEN_PATTERN,
  RECOVERY_EMAIL_CODE_PATTERN,
  recoveryCodeKey,
  type RecoveryMethod,
} from '@zion/sdk-contracts';
import { secondFactorRefusalOf } from '../platform/auth/auth.api';
import type { RecoveryProof } from '../platform/auth/useAuth';
import { displayMessage } from '../platform/errors';
import { AuthCardFrame } from '../shell/AuthCardFrame';
import { Button } from '../shell/Button';
import { FormField } from '../shell/FormField';
import { OneTimeSecretDialog } from '../shell/OneTimeSecretDialog';

/** What the recovery card shows: the chooser, or the form of one method. */
export type RecoveryView = 'choose' | RecoveryMethod;

/** The chooser label of each method. */
export const RECOVERY_METHOD_LABEL: Readonly<Record<RecoveryMethod, string>> = {
  code: 'Use a recovery code',
  email: 'Get a code by email',
  'break-glass': 'Use a break-glass token',
  'admin-reset': 'Ask another administrator',
};

/** The subtitle of the chooser. */
export const RECOVERY_CHOOSER_TEXT = 'Choose another way to confirm this sign-in.';

/** The text of a typed recovery code that `recoveryCodeKey` refuses. No request leaves. */
export const RECOVERY_CODE_FORMAT_TEXT = 'Enter the code as XXXX-XXXX-XXXX.';

/** The text of a typed mail code that is not six digits. No request leaves. */
export const RECOVERY_EMAIL_FORMAT_TEXT = 'Enter the six digits of the email.';

/** The text of a typed break-glass token that is not 43 base64url characters. No request leaves. */
export const BREAK_GLASS_FORMAT_TEXT = 'Enter the 43 characters of the token.';

/** The text of `recovery_invalid`, per method. The wait stays until the fifth miss. */
export const RECOVERY_INVALID_TEXT: Readonly<Record<Exclude<RecoveryMethod, 'admin-reset'>, string>> = {
  code: 'The recovery code did not match. Try again.',
  email: 'The emailed code did not match. Try again.',
  'break-glass': 'The break-glass token did not match. Try again.',
};

/** The text of `recovery_unavailable`. The wait stays, so another way is still open. */
export const RECOVERY_UNAVAILABLE_TEXT =
  'This sign-in step takes no proof of this kind now. Choose another way, or sign in again later.';

/** The notice after a mail send. */
export const RECOVERY_EMAIL_SENT_TEXT = 'The control plane sent a code to the email address of this account.';

/** The text of the `admin-reset` method. */
export const ADMIN_RESET_TEXT =
  'Ask another platform administrator to reset the passkeys of this account. They open Access, People, '
  + 'select this account and press Reset passkeys. Then sign in again and register a new passkey.';

/** The fallback of a failure with no text of its own. */
const RECOVERY_FAILED_TEXT = 'The recovery proof failed.';

/** A refusal that ends the wait: the page then shows its ended card. */
export type RecoveryEnded = 'expired' | 'inactive';

export interface SecondFactorRecoveryCardProps {
  /** The proofs that the wait takes, in `RECOVERY_METHODS` order. At least one. */
  readonly methods: readonly RecoveryMethod[];
  /** What the card shows now. The page holds it, so a re-render keeps the place. */
  readonly view: RecoveryView;
  /** Move to another view, or back to the passkey (`'passkey'`). */
  readonly onView: (view: RecoveryView | 'passkey') => void;
  /** `useAuth.proveRecovery`. A pass turns the wait into the `register` arm of the page. */
  readonly onProve: (proof: RecoveryProof) => Promise<void>;
  /** `useAuth.sendRecoveryEmail`. */
  readonly onSendEmail: () => Promise<void>;
  /** The wait ended on the server, or its expiry passed. */
  readonly onEnded: (arm: RecoveryEnded) => void;
  /** True when the wait is past its expiry: the card then sends nothing and calls `onEnded`. */
  readonly expired: () => boolean;
}

/**
 * The chooser and the proof forms, each as one pre-auth card.
 *
 * - `choose`: one button per method, and "Use your passkey", which goes back to the passkey.
 * - `code`, `email`, `break-glass`: a field, "Continue" and "Choose another way". `email` first
 *   offers "Send the code"; after a send it shows the field and "Send another code".
 * - `admin-reset`: `ADMIN_RESET_TEXT` and "Choose another way".
 *
 * Focus (WS-31b-R7): each view mounts its own frame (a `key` per view). The chooser, the
 * admin-reset text and the email card before a send move focus to the title (`autoFocusTitle`); a
 * form with a field moves it to the field (`autoFocus`), which the title would otherwise take back.
 *
 * Failures: `token_invalid` and a passed expiry call `onEnded('expired')`; `operator_inactive` calls
 * `onEnded('inactive')`; `recovery_invalid` shows `RECOVERY_INVALID_TEXT` of the method;
 * `recovery_unavailable` shows `RECOVERY_UNAVAILABLE_TEXT`; every other failure shows
 * `displayMessage`. The field keeps its text after a failure, so a typo is one key away.
 */
export function SecondFactorRecoveryCard({
  methods,
  view,
  onView,
  onProve,
  onSendEmail,
  onEnded,
  expired,
}: SecondFactorRecoveryCardProps): React.JSX.Element {
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mailSent, setMailSent] = useState(false);

  /** Change the view and clear what the last form showed. */
  function go(next: RecoveryView | 'passkey'): void {
    setValue('');
    setProblem(null);
    setError(null);
    setMailSent(false);
    onView(next);
  }

  /** Map a failure of a proof or a send to the card's arms. */
  function showFailure(cause: unknown, method: Exclude<RecoveryMethod, 'admin-reset'>): void {
    const refusal = secondFactorRefusalOf(cause);
    if (refusal === 'token_invalid') onEnded('expired');
    else if (refusal === 'operator_inactive') onEnded('inactive');
    else if (refusal === 'recovery_invalid') setError(RECOVERY_INVALID_TEXT[method]);
    else if (refusal === 'recovery_unavailable') setError(RECOVERY_UNAVAILABLE_TEXT);
    else setError(displayMessage(cause, RECOVERY_FAILED_TEXT));
  }

  /** Run `act` unless the wait has expired, with the busy flag and the failure map around it. */
  async function run(method: Exclude<RecoveryMethod, 'admin-reset'>, act: () => Promise<void>): Promise<void> {
    if (expired()) {
      onEnded('expired');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await act();
    } catch (cause) {
      showFailure(cause, method);
    } finally {
      setBusy(false);
    }
  }

  /** Check the typed text of `method` and send the proof, or show the format problem. */
  function submit(method: Exclude<RecoveryMethod, 'admin-reset'>): void {
    const typed = value.trim();
    let proof: RecoveryProof | null = null;
    if (method === 'code') {
      const key = recoveryCodeKey(typed);
      proof = key === null ? null : { method: 'code', code: key };
    } else if (method === 'email') {
      proof = RECOVERY_EMAIL_CODE_PATTERN.test(typed) ? { method: 'email', code: typed } : null;
    } else {
      proof = BREAK_GLASS_TOKEN_PATTERN.test(typed) ? { method: 'break-glass', token: typed } : null;
    }
    if (proof === null) {
      setProblem(FORMAT_TEXT[method]);
      return;
    }
    setProblem(null);
    const ready = proof;
    void run(method, () => onProve(ready));
  }

  function sendMail(): void {
    void run('email', async () => {
      await onSendEmail();
      setMailSent(true);
    });
  }

  const notice = error === null ? undefined : { verdict: 'bad' as const, message: error };
  const backToChooser = (
    <Button size="md" onClick={() => go('choose')} data-testid="second-factor-recovery-choose">
      Choose another way
    </Button>
  );

  if (view === 'choose') {
    return (
      <AuthCardFrame key="choose" title="Second sign-in step" subtitle={RECOVERY_CHOOSER_TEXT} autoFocusTitle testId="second-factor-recovery">
        <div className="flex flex-col gap-2">
          {methods.map((method) => (
            <Button key={method} size="md" onClick={() => go(method)} data-testid={`second-factor-recovery-${method}`}>
              {RECOVERY_METHOD_LABEL[method]}
            </Button>
          ))}
        </div>
        <Button size="md" tone="ghost" onClick={() => go('passkey')} data-testid="second-factor-recovery-passkey">
          Use your passkey
        </Button>
      </AuthCardFrame>
    );
  }

  if (view === 'admin-reset') {
    return (
      <AuthCardFrame
        key="admin-reset"
        title="Second sign-in step"
        subtitle={RECOVERY_METHOD_LABEL['admin-reset']}
        autoFocusTitle
        testId="second-factor-recovery"
      >
        <p data-testid="second-factor-recovery-admin-reset-text" className="max-w-[100ch] text-xs text-secondary">
          {ADMIN_RESET_TEXT}
        </p>
        {backToChooser}
      </AuthCardFrame>
    );
  }

  const form = FORMS[view];
  const askForMail = view === 'email' && !mailSent;
  const mailNotice = view === 'email' && mailSent && error === null ? { verdict: 'good' as const, message: RECOVERY_EMAIL_SENT_TEXT } : notice;
  return (
    <AuthCardFrame
      key={view}
      title="Second sign-in step"
      subtitle={form.subtitle}
      notice={mailNotice}
      autoFocusTitle={askForMail}
      testId="second-factor-recovery"
    >
      {askForMail ? (
        <Button tone="primary" size="md" disabled={busy} onClick={sendMail} data-testid="second-factor-recovery-send">
          Send the code
        </Button>
      ) : (
        <>
          <FormField
            label={form.label}
            name={form.name}
            value={value}
            onChange={setValue}
            testId="second-factor-recovery-field"
            required
            mono
            placeholder={form.placeholder}
            problem={problem}
            autoFocus
          />
          <Button
            tone="primary"
            size="md"
            disabled={busy || value.trim() === ''}
            onClick={() => submit(view)}
            data-testid="second-factor-recovery-submit"
          >
            Continue
          </Button>
          {view === 'email' ? (
            <Button size="md" disabled={busy} onClick={sendMail} data-testid="second-factor-recovery-resend">
              Send another code
            </Button>
          ) : null}
        </>
      )}
      {backToChooser}
    </AuthCardFrame>
  );
}

/** The format problem of each method. */
const FORMAT_TEXT: Readonly<Record<Exclude<RecoveryMethod, 'admin-reset'>, string>> = {
  code: RECOVERY_CODE_FORMAT_TEXT,
  email: RECOVERY_EMAIL_FORMAT_TEXT,
  'break-glass': BREAK_GLASS_FORMAT_TEXT,
};

/** The words of each proof form. */
const FORMS: Readonly<
  Record<
    Exclude<RecoveryMethod, 'admin-reset'>,
    { readonly subtitle: string; readonly label: string; readonly name: string; readonly placeholder: string }
  >
> = {
  code: {
    subtitle: 'Enter one of the recovery codes that you saved when you registered your passkey.',
    label: 'Recovery code',
    name: 'recoveryCode',
    placeholder: 'XXXX-XXXX-XXXX',
  },
  email: {
    subtitle: 'The control plane mails a six-digit code to the email address of this account.',
    label: 'Code from the email',
    name: 'recoveryEmailCode',
    placeholder: '123456',
  },
  'break-glass': {
    subtitle: 'Paste the one-time token that a platform owner made for this account with bin/cp-break-glass.',
    label: 'Break-glass token',
    name: 'breakGlassToken',
    placeholder: '43 characters',
  },
};

export interface RecoveryCodesCardProps {
  /** The ten codes of the verify answer. Shown once; the caller drops them on `onSaved`. */
  readonly codes: readonly string[];
  /**
   * A caution sentence above the codes, or `undefined` for none. `SecondFactorPage` passes
   * `SECOND_FACTOR_REDIRECT_CLOCK_TEXT` while a command-line sign-in waits behind the card.
   */
  readonly notice?: string;
  /** The operator saved the codes: the page then finishes the sign-in. */
  readonly onSaved: () => void;
}

/**
 * The ten recovery codes that a verify issued, shown once (R-152 Q-5), in `OneTimeSecretDialog`:
 * Escape does not dismiss it, and "Close" stays off until the operator ticks "I saved these recovery
 * codes." The console renders nothing else until then.
 */
export function RecoveryCodesCard({ codes, notice, onSaved }: RecoveryCodesCardProps): React.JSX.Element {
  return (
    <AuthCardFrame
      title="Save your recovery codes"
      subtitle="The passkey step passed. Save these codes before you continue."
      notice={notice === undefined ? undefined : { verdict: 'caution', message: notice }}
      autoFocusTitle
      testId="second-factor-codes-card"
    >
      <OneTimeSecretDialog
        testId="second-factor-codes"
        copyLabel="Copy the recovery codes"
        titleNode={
          <h2 id="second-factor-codes-title" className="text-sm font-semibold text-primary">
            Ten new recovery codes
          </h2>
        }
        bodyCopy={
          <p className="max-w-[100ch] text-xs text-secondary">
            Each code replaces your passkey once, if you lose it. The control plane shows these codes only now.
          </p>
        }
        secret={codes}
        ackLabel="I saved these recovery codes."
        onClose={onSaved}
      />
    </AuthCardFrame>
  );
}
